import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, IdentificationCard, Plus, X, CheckCircle, Warning, ArrowRight, Upload, Camera } from '@phosphor-icons/react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Guests-on-stream verification panel.
 *
 * Props:
 *   guests:        array of {performer_id, display_name, kind}
 *   onChange:      (newGuests) => void
 */
export default function GuestVerificationPanel({ guests, onChange }) {
  const [mode, setMode] = useState('idle'); // idle | lookup | verify
  const [lookupId, setLookupId] = useState('');
  const [looking, setLooking] = useState(false);
  const [savedRoster, setSavedRoster] = useState([]);

  // Verify-new-guest form
  const [form, setForm] = useState({
    full_legal_name: '',
    stage_name: '',
    date_of_birth: '',
    id_type: 'drivers_license',
  });
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const idInputRef = useRef(null);
  const selfieInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/verification/co-performers`, { withCredentials: true })
      .then(r => setSavedRoster(r.data || []))
      .catch(() => {});
  }, []);

  // Memoize roster filtered against currently-selected guests
  const availableRoster = useMemo(
    () => savedRoster.filter(r => !guests.some(g => g.performer_id === r.performer_id)),
    [savedRoster, guests],
  );

  const normalize = (raw) => {
    let p = (raw || '').trim().toUpperCase();
    if (!p.startsWith('LL-')) p = 'LL-' + p;
    return p;
  };

  const addGuest = (g) => {
    if (guests.some(x => x.performer_id === g.performer_id)) {
      toast.info(`${g.display_name} is already added`);
      return;
    }
    onChange([...guests, g]);
    toast.success(`${g.display_name} added as guest`);
  };

  const removeGuest = (pid) => {
    onChange(guests.filter(g => g.performer_id !== pid));
  };

  const doLookup = async () => {
    if (!lookupId.trim()) return;
    setLooking(true);
    try {
      const pid = normalize(lookupId);
      const res = await axios.get(`${API}/api/verification/lookup-performer/${pid}`, { withCredentials: true });
      if (res.data.found) {
        addGuest({
          performer_id: res.data.performer_id,
          display_name: res.data.display_name,
          kind: res.data.kind,
        });
        setLookupId('');
        setMode('idle');
      } else {
        toast.error(`Performer ID "${pid}" not found or not verified`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally {
      setLooking(false);
    }
  };

  const handleFile = (file, type) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    const url = URL.createObjectURL(file);
    if (type === 'id') { setIdFile(file); setIdPreview(url); }
    else { setSelfieFile(file); setSelfiePreview(url); }
  };

  const submitNewGuest = async () => {
    if (!form.full_legal_name.trim() || !form.date_of_birth) {
      toast.error('Legal name and DOB required'); return;
    }
    if (!idFile || !selfieFile) {
      toast.error('Both government ID and selfie are required'); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('full_legal_name', form.full_legal_name);
      fd.append('stage_name', form.stage_name);
      fd.append('date_of_birth', form.date_of_birth);
      fd.append('id_type', form.id_type);
      fd.append('id_file', idFile);
      fd.append('selfie_file', selfieFile);
      const res = await axios.post(`${API}/api/verification/co-performer`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      addGuest({
        performer_id: res.data.performer_id,
        display_name: res.data.stage_name,
        kind: 'guest',
      });
      // Refresh roster
      const r = await axios.get(`${API}/api/verification/co-performers`, { withCredentials: true });
      setSavedRoster(r.data || []);
      // Reset
      setForm({ full_legal_name: '', stage_name: '', date_of_birth: '', id_type: 'drivers_license' });
      setIdFile(null); setSelfieFile(null); setIdPreview(null); setSelfiePreview(null);
      setMode('idle');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="guest-verification-panel">
      {/* Current guests pinned */}
      {guests.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {guests.map(g => (
            <span
              key={g.performer_id}
              className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-500/15 text-emerald-300 text-xs rounded-full ring-1 ring-emerald-500/30"
              data-testid={`guest-chip-${g.performer_id}`}
            >
              <CheckCircle weight="fill" className="w-3 h-3" />
              {g.display_name}
              <span className="text-emerald-400/70 font-mono">{g.performer_id}</span>
              <button onClick={() => removeGuest(g.performer_id)} className="hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Saved roster quick-add */}
      {savedRoster.length > 0 && mode === 'idle' && (
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Your verified guest roster</p>
          <div className="flex flex-wrap gap-1.5">
            {savedRoster.filter(r => !guests.some(g => g.performer_id === r.performer_id)).map(r => (
              <button
                key={r.performer_id}
                type="button"
                onClick={() => addGuest({ performer_id: r.performer_id, display_name: r.stage_name, kind: 'guest' })}
                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-xs rounded-lg text-slate-300 flex items-center gap-1"
                data-testid={`roster-add-${r.performer_id}`}
              >
                <Plus className="w-3 h-3" /> {r.stage_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mode selector */}
      {mode === 'idle' && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('lookup')}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-sm text-white rounded-lg transition-colors"
            data-testid="guest-mode-lookup"
          >
            <IdentificationCard className="w-4 h-4" /> Enter Performer ID
          </button>
          <button
            type="button"
            onClick={() => setMode('verify')}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-sm text-blue-300 rounded-lg ring-1 ring-blue-500/30 transition-colors"
            data-testid="guest-mode-verify"
          >
            <Plus className="w-4 h-4" /> Verify New Guest
          </button>
        </div>
      )}

      {/* Lookup mode */}
      {mode === 'lookup' && (
        <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
          <p className="text-xs text-slate-400">Enter your collab partner's Performer ID</p>
          <div className="flex gap-2">
            <Input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="LL-A3F9KX2P"
              className="bg-slate-900 border-white/10 flex-1 font-mono uppercase"
              data-testid="guest-lookup-input"
            />
            <button
              type="button"
              onClick={doLookup}
              disabled={looking || !lookupId.trim()}
              className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center gap-1"
              data-testid="guest-lookup-submit"
            >
              {looking ? 'Looking…' : <>Add <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button
              type="button"
              onClick={() => { setMode('idle'); setLookupId(''); }}
              className="px-3 py-2 text-slate-400 hover:text-white text-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Verify-new-guest mode */}
      {mode === 'verify' && (
        <div className="bg-slate-800/50 rounded-lg p-3 space-y-3">
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5">
            <Warning weight="fill" className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/90">
              Required for 2257 compliance. Upload your guest's gov ID + selfie before streaming with them.
              Failure to do so risks an account ban.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Legal Name *</Label>
              <Input
                value={form.full_legal_name}
                onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })}
                placeholder="First Last"
                className="bg-slate-900 border-white/10 mt-1 text-sm"
                data-testid="guest-legal-name"
              />
            </div>
            <div>
              <Label className="text-xs">Stage Name</Label>
              <Input
                value={form.stage_name}
                onChange={(e) => setForm({ ...form, stage_name: e.target.value })}
                placeholder="(optional)"
                className="bg-slate-900 border-white/10 mt-1 text-sm"
                data-testid="guest-stage-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date of Birth *</Label>
              <Input
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                className="bg-slate-900 border-white/10 mt-1 text-sm"
                data-testid="guest-dob"
              />
            </div>
            <div>
              <Label className="text-xs">ID Type</Label>
              <select
                value={form.id_type}
                onChange={(e) => setForm({ ...form, id_type: e.target.value })}
                className="w-full bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 mt-1 text-sm text-white"
                data-testid="guest-id-type"
              >
                <option value="drivers_license">Driver's License</option>
                <option value="passport">Passport</option>
                <option value="id_card">National ID</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <input
                ref={idInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0], 'id')}
              />
              <button
                type="button"
                onClick={() => idInputRef.current?.click()}
                className="w-full border-2 border-dashed border-white/10 rounded-lg p-3 hover:border-blue-500/50 transition-colors text-center"
                data-testid="guest-id-upload"
              >
                {idPreview ? (
                  <img src={idPreview} alt="ID" className="max-h-20 mx-auto rounded" />
                ) : (
                  <div className="py-2">
                    <Upload className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                    <p className="text-[10px] text-slate-400">Gov ID</p>
                  </div>
                )}
              </button>
            </div>
            <div>
              <input
                ref={selfieInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="user"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0], 'selfie')}
              />
              <button
                type="button"
                onClick={() => selfieInputRef.current?.click()}
                className="w-full border-2 border-dashed border-white/10 rounded-lg p-3 hover:border-blue-500/50 transition-colors text-center"
                data-testid="guest-selfie-upload"
              >
                {selfiePreview ? (
                  <img src={selfiePreview} alt="Selfie" className="max-h-20 mx-auto rounded" />
                ) : (
                  <div className="py-2">
                    <Camera className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                    <p className="text-[10px] text-slate-400">Selfie + ID</p>
                  </div>
                )}
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('idle')}
              className="px-3 py-2 text-slate-400 hover:text-white text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitNewGuest}
              disabled={submitting}
              className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="guest-verify-submit"
            >
              {submitting ? 'Verifying…' : <><CheckCircle weight="fill" className="w-4 h-4" /> Verify & Add</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
